import * as React from "react";
import {
    LoadComponentStateAsync,
    SaveComponentStateAsync,
} from "../../infrastructure/Data/MotwIndexedDb";
import { ESharedComponent } from "../../services/UI/State/ESharedComponent";
import { State } from "../../services/UI/State/State";
import { BottomBarRender } from "./Render/BottomBarRender";
import { BottomBarViewportManager } from "./Services/BottomBarViewport";
import { BottomBarThemeMediaQuery } from "./Services/BottomBarThemeMediaQuery";
import { IBottomBarState } from "./IBottomBarState";
import {
    LoadStoredQuickJournalThemeName,
    NormalizeQuickJournalThemeSelectionName,
} from "./QuickJournal/View/QuickJournalTheme";
import { GetLocalDayHourKey } from "./QuickJournal/Data/QuickJournalKeys";
import {
    bottomBarCloseDurationInMilliseconds,
    quickJournalHeatmapFlashDurationInMilliseconds,
} from "./Services/BottomBarStyles";
import { GetQuickJournalNow } from "./QuickJournal/Data/Clock/QuickJournalClock";
import { IsIosDevice } from "./Services/BottomBarHelpers";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import Background from "source/app/services/UI/Background/Background";
interface IBottomBarProps {
    onDisplayLargeChange?: (displayLarge: boolean) => void;
}
export class BottomBarContainer extends React.PureComponent<
    IBottomBarProps,
    IBottomBarState
> {
    private viewportManager = new BottomBarViewportManager();
    private isClosingQuickJournal = false;
    private closeQuickJournalTimeout: number | undefined;
    private quickJournalHasFocus = false;
    private quickJournalHeatmapFlashTimeout: number | undefined;
    private themeMediaQuery: BottomBarThemeMediaQuery;
    private clockChanged = () => this.forceUpdate();
    private themeChanged = () => {
        if (this.state.quickJournalThemeName !== "auto") return;
        if (IsMobileDevice()) {
            Background.ChangeImage("auto");
        }
        this.forceUpdate();
    };
    private refreshViewport = (force: boolean) =>
        this.computeBottomInset(force);
    constructor(props: IBottomBarProps) {
        super(props);
        this.themeMediaQuery = new BottomBarThemeMediaQuery(this.themeChanged);
        this.state = {
            showQuickJournal: false,
            displayParticles: false,
            quickJournalThemeName: LoadStoredQuickJournalThemeName(),
            quickJournalRecentEntries: [],
            quickJournalWeeklyHourlyCounts: {},
        };
    }
    async componentDidMount() {
        await LoadComponentStateAsync(this);
        const normalizedName = NormalizeQuickJournalThemeSelectionName(
            this.state.quickJournalThemeName
        );
        if (normalizedName !== this.state.quickJournalThemeName) {
            this.setState({ quickJournalThemeName: normalizedName });
        }
        if (IsMobileDevice()) {
            Background.ChangeImage(normalizedName);
        }
        this.viewportManager.connect(this.refreshViewport);
        this.connectClockListener();
        this.themeMediaQuery.connect();
        this.viewportManager.scheduleRefresh(this.refreshViewport);
        State.ShareClass(ESharedComponent.BottomBar, this);
        this.props.onDisplayLargeChange?.(this.state.showQuickJournal);
    }
    async componentDidUpdate(
        prevProps: IBottomBarProps,
        prevState: IBottomBarState
    ) {
        await SaveComponentStateAsync(this);
        State.ShareClass(ESharedComponent.BottomBar, this);
        if (prevState.showQuickJournal !== this.state.showQuickJournal) {
            if (this.state.showQuickJournal) {
                this.finishClosingQuickJournal();
            }
            this.props.onDisplayLargeChange?.(this.state.showQuickJournal);
        }
    }
    componentWillUnmount() {
        this.viewportManager.disconnect();
        this.disconnectClockListener();
        this.themeMediaQuery.disconnect();
        this.viewportManager.clearRefreshTimeouts();
        window.clearTimeout(this.quickJournalHeatmapFlashTimeout);
        window.clearTimeout(this.closeQuickJournalTimeout);
    }
    private computeBottomInset(force: boolean) {
        const previous = this.viewportManager.bottomInset;
        const next = this.viewportManager.computeBottomInset(force, previous);
        if (next === previous) return;
        this.forceUpdate();
    }
    private connectClockListener() {
        window.addEventListener("quickjournal-system-clock-changed", this.clockChanged);
    }
    private disconnectClockListener() {
        window.removeEventListener("quickjournal-system-clock-changed", this.clockChanged);
    }

    private setQuickJournalFocus = (hasFocus: boolean) => {
        if (this.quickJournalHasFocus === hasFocus) return;
        this.quickJournalHasFocus = hasFocus;
        if (this.isClosingQuickJournal) return;
        if (hasFocus) {
            this.viewportManager.scheduleRefresh(this.refreshViewport);
        }
        this.forceUpdate();
    };

    private handleQuickJournalEntrySubmitted = () => {
        const now = GetQuickJournalNow();
        const flashCellKey = GetLocalDayHourKey(now.toISOString());
        this.setState({
            quickJournalHeatmapFlashCellKey: flashCellKey,
            quickJournalHeatmapFlashStartedAt: Date.now(),
        });
        if (this.quickJournalHeatmapFlashTimeout) {
            window.clearTimeout(this.quickJournalHeatmapFlashTimeout);
        }
        this.quickJournalHeatmapFlashTimeout = window.setTimeout(() => {
            this.setState({
                quickJournalHeatmapFlashCellKey: undefined,
                quickJournalHeatmapFlashStartedAt: undefined,
            });
            this.quickJournalHeatmapFlashTimeout = undefined;
            this.forceUpdate();
        }, quickJournalHeatmapFlashDurationInMilliseconds);
    };

    private closeQuickJournal = () => {
        if (this.isClosingQuickJournal) return;
        this.isClosingQuickJournal = true;
        window.clearTimeout(this.closeQuickJournalTimeout);
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (IsIosDevice()) {
            this.viewportManager.scheduleRefresh(this.refreshViewport);
        }
        this.quickJournalHasFocus = false;
        this.setState({ showQuickJournal: false }, () => {
            if (bottomBarCloseDurationInMilliseconds <= 0) {
                this.finishClosingQuickJournal();
                return;
            }
            this.closeQuickJournalTimeout = window.setTimeout(() => {
                this.finishClosingQuickJournal();
            }, bottomBarCloseDurationInMilliseconds);
        });
    };

    private finishClosingQuickJournal = () => {
        window.clearTimeout(this.closeQuickJournalTimeout);
        this.closeQuickJournalTimeout = undefined;
        if (!this.isClosingQuickJournal) {
            return;
        }

        this.isClosingQuickJournal = false;
        this.forceUpdate();
    };

    render() {
        return (
            <BottomBarRender
                state={this.state}
                quickJournalHasFocus={this.quickJournalHasFocus}
                isClosingQuickJournal={this.isClosingQuickJournal}
                bottomInset={this.viewportManager.bottomInset}
                onThemeNameChange={(name) =>
                    this.setState({ quickJournalThemeName: name }, () => {
                        if (IsMobileDevice()) {
                            Background.ChangeImage(name);
                        }
                    })
                }
                onKeyboardFocusChange={this.setQuickJournalFocus}
                onEntrySubmitted={this.handleQuickJournalEntrySubmitted}
                onRecentEntriesChange={(recentEntries, weeklyHourlyCounts) =>
                    this.setState({
                        quickJournalRecentEntries: recentEntries,
                        quickJournalWeeklyHourlyCounts: weeklyHourlyCounts,
                    })
                }
                onCloseQuickJournal={this.closeQuickJournal}
            />
        );
    }
}
